import { AnalyticsSDK } from '@oneclick/analytics-sdk';

/**
 * Analytics Service for Tangerine Frontoffice
 * Provides a centralized way to send analytics events using the @oneclick/analytics-sdk
 */
class AnalyticsService {
  constructor() {
    this.client = null;
    this.isInitialized = false;
    this.defaultUser = null;
    this.defaultContext = null;
  }

  /**
   * Initialize the analytics service
   * @param {Object} config - Configuration object
   * @param {string} config.endpoint - Analytics endpoint URL
   * @param {Object} [config.defaultUser] - Default user information
   * @param {Object} [config.defaultContext] - Default context information
   */
  initialize(config) {
    if (this.isInitialized) {
      console.warn('AnalyticsService already initialized');
      return;
    }

    try {
      this.client = AnalyticsSDK.createClient({
        endpoint: config.endpoint,
        timeout: 10000,
        retryAttempts: 3,
        retryDelay: 1000,
      });

      this.defaultUser = config.defaultUser;
      this.defaultContext = config.defaultContext;
      this.isInitialized = true;

      console.log('AnalyticsService initialized successfully');
    } catch (error) {
      console.error('Failed to initialize AnalyticsService:', error);
      throw error;
    }
  }

  /**
   * Send an analytics event
   * @param {string} eventType - Type of event (from EventType enum)
   * @param {Object} options - Event options
   * @param {string} options.tenant - Tenant identifier
   * @param {Object} options.user - User information
   * @param {Object} options.context - Context information
   * @param {Object} options.payload - Payload data
   * @returns {Promise<Object>} Response from analytics service
   */
  async trackEvent(eventType, options) {
    if (!this.isInitialized) {
      throw new Error('AnalyticsService not initialized. Call initialize() first.');
    }

    try {
      const event = AnalyticsSDK.createEvent(eventType)
        .withTenant(options.tenant)
        .withUser(options.user)
        .withContext(options.context)
        .withPayload(options.payload || {});

      const response = await event.send(this.client);
      return response;
    } catch (error) {
      console.error(`Failed to send event ${eventType}:`, error);
      throw error;
    }
  }

  /**
   * Get current initialization status
   * @returns {boolean} Whether the service is initialized
   */
  getInitializationStatus() {
    return this.isInitialized;
  }
}

// Export singleton instance
export default new AnalyticsService();
